import { Box, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import {
  BankingEntitiesBankAccountNumberFormatEnum,
  DataSchemaItemFragment,
} from 'generated/resources/graphql'
import { camelCase } from 'lodash'

import DayPicker from 'modules/core/components/DayPicker'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import { accountNumberExtraFieldLabel } from 'modules/banking/lib/bankAccountNumbersHelpers'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { WizardData } from 'modules/employees/components/UnifiedAddEmployeeModal/types'
import { getStringFieldValue } from 'modules/employees/components/UnifiedAddEmployeeModal/utils'
import { Salary } from 'modules/job_catalog/lib/salary'
import { WorkingConditions } from 'modules/job_catalog/lib/workingConditions'

import { AnnualWorkingTimeDistributionField } from './AnnualWorkingTimeDistributionField'
import { BankHolidayTreatmentField } from './BankHolidayTreatmentField'
import { BaseSalaryTypeField } from './BaseSalaryTypeField'
import { DaysSelectorField } from './DaysSelectorField'
import { EnumField } from './EnumField'
import { IntegerField } from './IntegerField'

interface Props {
  field: DataSchemaItemFragment
  data: WizardData
  onChangeField: (fieldName: string, value: string) => void
  onMoneyChange?: (value: string, frequency?: string) => void
  onClickToggle?: (fieldName: string, value: boolean) => void
  getFieldError?: (fieldName: keyof WizardData) => string | undefined
  ref?: React.RefObject<HTMLDivElement>
  handleOnBlur?: () => void
  handleOnFocus?: () => void
  maxDate?: Date | moment.Moment
  handleEmployeeSelect?: (fieldName: string, employee: number | undefined) => void
  employeesValue?: number
  workingConditionName?: string
  workingConditions?: WorkingConditions
  includeInactive?: boolean
  precedenceSalaryRange?: Salary
}

export const Fields = ({
  field,
  data,
  onChangeField,
  onClickToggle,
  getFieldError,
  ref,
  handleOnBlur,
  handleOnFocus,
  maxDate,
  handleEmployeeSelect,
  employeesValue,
  workingConditions,
  includeInactive = true,
}: Props) => {
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const fieldName = camelCase(field.fieldName) as keyof WizardData

  // German base salary type field (uses PayrollConceptSelect)
  if (field.fieldName === 'contracts_de_base_salary_type_id') {
    return (
      <BaseSalaryTypeField
        field={field}
        fieldName={fieldName}
        data={data}
        onChangeField={onChangeField}
      />
    )
  }

  const getPlaceholder = () => {
    if (field.section === 'bank_information') {
      if (field.fieldName === 'bank_number') {
        return i18n.t('employee_form.bank_info.placeholder')
      }
    }
    if (field.section === 'identification_information') {
      if (field.fieldName === 'identifier') {
        return i18n.t('employee_form.identifier_placeholder')
      }
    }
    // Add more fields as needed
    return undefined
  }

  switch (field.fieldType) {
    case 'string':
      return (
        <TextField
          key={field.fieldName}
          label={field.label}
          value={getStringFieldValue(fieldName, data) || ''}
          onChange={(value) => onChangeField(fieldName, value)}
          error={getFieldError ? getFieldError(fieldName) : undefined}
          mandatory={field.required}
          placeholder={getPlaceholder()}
        />
      )
    case 'swift':
      const label = accountNumberExtraFieldLabel(
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        data.bankNumberFormat as BankingEntitiesBankAccountNumberFormatEnum
      )
      if (!data.bankNumberFormat || !label) {
        return null
      }
      return (
        <TextField
          key={field.fieldName}
          label={label}
          value={getStringFieldValue(fieldName, data) || ''}
          onChange={(value) => onChangeField(fieldName, value)}
          error={getFieldError ? getFieldError(fieldName) : undefined}
          mandatory={field.required}
        />
      )
    case 'email':
      return (
        <TextField
          key={field.fieldName}
          type="email"
          label={i18n.t('add_employee_modal.general_information_step.fields.email.label')}
          value={data?.email}
          error={getFieldError ? getFieldError(fieldName) : undefined}
          onChange={(value) => onChangeField('email', value)}
          onBlur={handleOnBlur}
          onFocus={handleOnFocus}
          mandatory
        />
      )
    case 'enum':
      return (
        <EnumField
          key={field.fieldName}
          dataSchemaItem={field}
          value={getStringFieldValue(fieldName, data) || ''}
          onChange={onChangeField}
          mandatory={field.required}
          legalEntityId={data?.legalEntityId ? +data.legalEntityId : undefined}
          country={data.legalEntityCountry}
          error={getFieldError ? getFieldError(fieldName) : undefined}
        />
      )
    case 'boolean':
      return (
        <TogglerCheckbox
          key={field.fieldName}
          label={field.label}
          value={!!data[fieldName]}
          onChange={(value) => onClickToggle?.(fieldName, value)}
        />
      )
    case 'date':
      return (
        <Box ref={ref} width="full">
          <DayPicker
            key={field.fieldName}
            mandatory={field.required}
            label={field.label}
            mode="single"
            value={getStringFieldValue(fieldName, data) || ''}
            maxDate={maxDate}
            onChange={(value) => onChangeField(fieldName, value)}
            withNullButtonLabel={i18n.t('common.delete')}
            withNull
          />
        </Box>
      )
    case 'integer':
      if (field.allowedValues) {
        return (
          <EnumField
            key={field.fieldName}
            dataSchemaItem={field}
            value={getStringFieldValue(fieldName, data) || ''}
            onChange={onChangeField}
            mandatory={field.required}
            legalEntityId={data?.legalEntityId ? +data.legalEntityId : undefined}
            country={data.legalEntityCountry}
            error={getFieldError ? getFieldError(fieldName) : undefined}
          />
        )
      }

      return (
        <IntegerField
          fieldName={fieldName}
          field={field}
          data={data}
          onChangeField={onChangeField}
          getFieldError={getFieldError}
          workingConditions={workingConditions}
        />
      )
    case 'days':
      return (
        <DaysSelectorField
          key={field.fieldName}
          dataSchemaItem={field}
          values={getStringFieldValue(fieldName, data) || ''}
          onChange={onChangeField}
        />
      )
    case 'employees':
      if (!handleEmployeeSelect) {
        return null
      }
      return (
        <EmployeeSelectorV2
          onChange={(employeeId) => handleEmployeeSelect(fieldName, employeeId || undefined)}
          label={field.label}
          options={{ hideSearchIcon: true }}
          value={employeesValue}
        />
      )
    case 'bank_holiday_treatment':
      return (
        <BankHolidayTreatmentField
          fieldName={fieldName}
          field={field}
          data={data}
          onChangeField={onChangeField}
          workingConditions={workingConditions}
        />
      )
    case 'annual_working_time_distribution':
      return (
        <AnnualWorkingTimeDistributionField
          fieldName={fieldName}
          data={data}
          onChangeField={onChangeField}
        />
      )
    default:
      return null
  }
}
